import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Star, Moon, Users, MessageCircle, Compass, CalendarDays, Hash,
  Infinity, NotebookPen, Layers, GraduationCap, RotateCcw, Eclipse,
  PawPrint, Globe, Sparkles, Dna, Briefcase, BookOpen, Feather,
  Hand, Rocket, Search, X,
} from 'lucide-react';

interface PaletteItem {
  label: string;
  description: string;
  path: string;
  keywords: string;
  icon: React.ElementType;
}

const ALL_TOOLS: PaletteItem[] = [
  { label: 'Mapa Astral',        description: 'Seu mapa natal completo com interpretação de IA',    path: '/birth-chart',      keywords: 'mapa natal nascimento planetas signos casas',      icon: Star },
  { label: 'Previsão',           description: 'O que o céu reserva para você hoje, semana e mês',  path: '/forecast',         keywords: 'previsão futuro trânsito hoje semana mês',           icon: Moon },
  { label: 'Sinastria',          description: 'Compatibilidade astrológica entre duas pessoas',     path: '/synastry',         keywords: 'sinastria compatibilidade amor relacionamento casal', icon: Users },
  { label: 'Perguntas',          description: 'Faça uma pergunta e receba uma resposta dos astros', path: '/questions',        keywords: 'perguntas oráculo resposta astros consulta',         icon: MessageCircle },
  { label: 'AstroMatch',         description: 'Encontre sua compatibilidade com outros signos',     path: '/astromatch',       keywords: 'astromatch match compatibilidade signo amor',         icon: Compass },
  { label: 'Tarot',              description: 'Tiragem de cartas com interpretação personalizada',  path: '/tarot',            keywords: 'tarot cartas tiragem leitura arcanos',               icon: Layers },
  { label: 'Lenormand',          description: 'Oráculo Lenormand com leitura por IA',              path: '/lenormand',        keywords: 'lenormand cartas oráculo leitura previsão',           icon: Layers },
  { label: 'Numerologia',        description: 'Seu número de vida e destino numerológico',         path: '/numerology',       keywords: 'numerologia número vida destino vibração',           icon: Hash },
  { label: 'I Ching',            description: 'O livro das mutações como guia de sabedoria',       path: '/i-ching',          keywords: 'i ching hexagramas sabedoria mudança oráculo',        icon: Infinity },
  { label: 'Diário dos Sonhos',  description: 'Registre e interprete seus sonhos com IA',          path: '/dream-journal',    keywords: 'sonhos diário registro interpretação dormir',         icon: NotebookPen },
  { label: 'Fases da Lua',       description: 'Calendário lunar com rituais e intenções',          path: '/moon-phases',      keywords: 'lua fases lunar calendário rituais novilúnio plenilúnio', icon: Moon },
  { label: 'Agenda Astrológica', description: 'Eventos celestiais importantes do mês',             path: '/agenda',           keywords: 'agenda eventos astrológica calendário mês',           icon: CalendarDays },
  { label: 'Retrógrado',         description: 'Radar de planetas retrógrados e seus efeitos',      path: '/retrogrado',       keywords: 'retrógrado mercúrio planetas efeitos retrogradação',  icon: RotateCcw },
  { label: 'Eclipses',           description: 'Previsão e impacto dos eclipses na sua vida',       path: '/eclipses',         keywords: 'eclipses solar lunar impacto portal energia',         icon: Eclipse },
  { label: 'Astro Pet',          description: 'Mapa astral do seu pet favorito',                   path: '/astro-pet',        keywords: 'pet animal astro mapa cachorro gato nascimento',      icon: PawPrint },
  { label: 'Astro Coach',        description: 'Coach pessoal baseado no seu mapa natal',           path: '/astro-coach',      keywords: 'coach pessoal crescimento autoconhecimento guia',     icon: GraduationCap },
  { label: 'Astrocartografia',   description: 'Descubra onde no mundo você brilha mais',           path: '/astrocartografia', keywords: 'astrocartografia mapa mundo local cidade país viagem', icon: Globe },
  { label: 'Human Design',       description: 'Seu tipo, estratégia e autoridade interior',        path: '/human-design',     keywords: 'human design tipo estratégia autoridade centros',     icon: Dna },
  { label: 'Astrologia Védica',  description: 'Mapa natal na tradição védica indiana',             path: '/vedic-astrology',  keywords: 'védica jyotish india vedic signo ascendente rashi',   icon: Sparkles },
  { label: 'Quiromancia',        description: 'Leitura de mãos com interpretação de IA',           path: '/quiromancy',       keywords: 'quiromancia mãos linhas palma leitura mão',           icon: Hand },
  { label: 'Grafologia',         description: 'Análise de personalidade pela escrita',             path: '/birth-chart',      keywords: 'grafologia escrita personalidade análise letra',       icon: Feather },
  { label: 'Start Astral',       description: 'Rota de desenvolvimento pessoal astrológico',       path: '/rota-profissional',keywords: 'start astral rota desenvolvimento pessoal estudo',    icon: Rocket },
  { label: 'Astro Journal',      description: 'Diário pessoal com reflexões astrológicas',         path: '/astro-journal',    keywords: 'journal diário reflexões escrita pessoal',            icon: BookOpen },
  { label: 'Empresas',           description: 'Astrologia aplicada a negócios e equipes',          path: '/empresas',         keywords: 'empresas negócios equipe astrologia corporativa',     icon: Briefcase },
  { label: 'Leituras',           description: 'Histórico de todas as suas leituras de IA',         path: '/readings',         keywords: 'leituras histórico análises relatórios passadas',     icon: BookOpen },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export function CommandPalette({ isOpen, onClose }: Props) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (!isOpen) return;
        onClose();
      }
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const q = query.trim().toLowerCase();
  const results = q
    ? ALL_TOOLS.filter(
        t => t.label.toLowerCase().includes(q) ||
             t.description.toLowerCase().includes(q) ||
             t.keywords.toLowerCase().includes(q)
      )
    : ALL_TOOLS;

  function go(path: string) {
    navigate(path);
    onClose();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Buscar ferramentas"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '10vh 1rem 2rem',
        backgroundColor: 'var(--bg-overlay)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '560px',
          backgroundColor: 'var(--bg-elevated)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-default)',
          boxShadow: 'var(--shadow-modal)',
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Search input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.875rem 1rem', borderBottom: '1px solid var(--border-subtle)' }}>
          <Search size={18} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} aria-hidden />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Buscar ferramentas..."
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: 'var(--text-primary)',
              fontSize: 'var(--text-body)',
              fontFamily: 'var(--font-ui)',
            }}
            aria-label="Buscar ferramentas"
          />
          <button
            onClick={onClose}
            aria-label="Fechar busca"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0.25rem', borderRadius: 'var(--radius-sm)' }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Results */}
        <div
          role="listbox"
          style={{ maxHeight: '60vh', overflowY: 'auto', padding: '0.5rem' }}
          aria-label="Resultados da busca"
        >
          {results.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', fontSize: 'var(--text-body-sm)' }}>
              Nenhuma ferramenta encontrada.
            </p>
          ) : (
            results.map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path + item.label}
                  role="option"
                  onClick={() => go(item.path)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.875rem',
                    padding: '0.75rem',
                    borderRadius: 'var(--radius-md)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background var(--transition-fast)',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(31,78,95,0.07)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <span style={{ color: 'var(--petrol)', flexShrink: 0 }}>
                    <Icon size={18} aria-hidden />
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', color: 'var(--text-primary)', fontSize: 'var(--text-label)', fontWeight: 500 }}>
                      {item.label}
                    </span>
                    <span style={{ display: 'block', color: 'var(--text-tertiary)', fontSize: 'var(--text-caption)', marginTop: '0.1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.description}
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </div>

        {/* Footer hint */}
        <div style={{ padding: '0.5rem 1rem', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: '1rem' }}>
          <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}><kbd style={{ background: 'var(--bg-surface)', padding: '1px 5px', borderRadius: '4px', border: '1px solid var(--border-subtle)', fontSize: '11px' }}>↑↓</kbd> navegar</span>
          <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}><kbd style={{ background: 'var(--bg-surface)', padding: '1px 5px', borderRadius: '4px', border: '1px solid var(--border-subtle)', fontSize: '11px' }}>↵</kbd> abrir</span>
          <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}><kbd style={{ background: 'var(--bg-surface)', padding: '1px 5px', borderRadius: '4px', border: '1px solid var(--border-subtle)', fontSize: '11px' }}>Esc</kbd> fechar</span>
        </div>
      </div>
    </div>
  );
}
